import { openDB, DBSchema, IDBPDatabase } from 'idb'

interface IMKIDatabase extends DBSchema {
  offlineQueue: {
    key: number
    value: OfflineQueueItem
  }
  timingData: {
    key: string
    value: {
      times: number[]
      average: number
      lastUpdated: number
    }
  }
  defectSolutions: {
    key: string
    value: {
      defectType: string
      title: string
      description: string
      solutions: Array<{
        image: Blob
        caption: string
        steps: string[]
      }>
      normen?: string[]
      estimatedCost?: { min: number; max: number; unit: string }
    }
  }
  intents: {
    key: string
    value: IntentRecord
  }
}

export type IntentType =
  | 'upload-image'
  | 'run-analysis'
  | 'send-ticket-email'
  | 'generate-inspection-pdf'
  | 'upload-floor-plan'
  | 'invite-user'
  | 'delete-user'
  | 'delete-defect'
  | 'upload-training-image'
  | 'trigger-reminder-run'
  | 'save-slides-settings'
  | 'report-dead-letter'

export type IntentStatus = 'pending' | 'in_flight' | 'failed' | 'done'

export interface IntentRecord {
  id: string
  type: IntentType
  payload: Record<string, unknown>
  dependsOn?: string
  status: IntentStatus
  attempts: number
  lastError?: string
  createdAt: number
  updatedAt: number
  inFlightSince?: number
  nextRunAt?: number
}

type DBEvent = 'blocked' | 'blocking' | 'terminated'
let dbEventListener: ((ev: DBEvent) => void) | null = null
export function onDBEvent(listener: (ev: DBEvent) => void): () => void {
  dbEventListener = listener
  return () => { if (dbEventListener === listener) dbEventListener = null }
}
function emitDBEvent(ev: DBEvent) {
  try { dbEventListener?.(ev) } catch { /* noop */ }
}

let db: IDBPDatabase<IMKIDatabase> | null = null

// Release the module-level IDB handle so `indexedDB.deleteDatabase('imki-db')`
// doesn't hit `onblocked` and silently no-op. Only the debug nuke flow calls
// this — the normal `blocking()` callback above handles rotation during
// upgrades.
export function closeDB(): void {
  try { db?.close() } catch { /* noop */ }
  db = null
}

const LEGACY_STORES = ['analyses', 'settings', 'projects'] as const

export async function initDB(): Promise<IDBPDatabase<IMKIDatabase>> {
  if (db) return db

  db = await openDB<IMKIDatabase>('imki-db', 6, {
    upgrade(db, oldVersion, _newVersion, tx) {
      if (oldVersion < 5) {
        for (const name of LEGACY_STORES) {
          if (db.objectStoreNames.contains(name as never)) {
            db.deleteObjectStore(name as never)
          }
        }
      }

      if (!db.objectStoreNames.contains('offlineQueue')) {
        const queueStore = db.createObjectStore('offlineQueue', {
          keyPath: 'id',
          autoIncrement: true,
        })
        // @ts-expect-error -- idb generic typing rejects dynamic index access
        queueStore.createIndex('by-status', 'status')
      }

      if (!db.objectStoreNames.contains('timingData')) {
        db.createObjectStore('timingData', { keyPath: 'key' })
      }

      if (!db.objectStoreNames.contains('defectSolutions')) {
        db.createObjectStore('defectSolutions', {
          keyPath: 'defectType',
        })
      }

      // v6: intents store + offlineQueue by-analysisId index for Phase 3 intent queue.
      if (oldVersion < 6) {
        if (!db.objectStoreNames.contains('intents')) {
          const intentStore = db.createObjectStore('intents', { keyPath: 'id' })
          // @ts-expect-error -- idb generic typing rejects dynamic index access
          intentStore.createIndex('by-status', 'status')
          // @ts-expect-error -- idb generic typing rejects dynamic index access
          intentStore.createIndex('by-type', 'type')
          // @ts-expect-error -- idb generic typing rejects dynamic index access
          intentStore.createIndex('by-createdAt', 'createdAt')
        }

        // Index offlineQueue by uploadedAnalysisId for fast blob lookup by analysis id.
        try {
          const queueStore = tx.objectStore('offlineQueue')
          // @ts-expect-error — idb's generic typing rejects adding an index with no corresponding schema entry.
          if (!queueStore.indexNames.contains('by-analysisId')) {
            // @ts-expect-error -- idb generic typing rejects dynamic index access
            queueStore.createIndex('by-analysisId', 'uploadedAnalysisId')
          }
        } catch { /* store may not exist yet on fresh installs; createObjectStore above will run with the index on next iteration. */ }
      }
    },
    // Another tab has an older connection and is blocking our upgrade.
    // Notify the UI so the user can close that tab.
    blocked() {
      emitDBEvent('blocked')
    },
    // A newer DB version wants to open. Close our handle so it can proceed.
    blocking() {
      emitDBEvent('blocking')
      try { db?.close() } catch { /* noop */ }
      db = null
    },
    // Connection terminated (usually because the backing store was cleared).
    terminated() {
      emitDBEvent('terminated')
      db = null
    },
  })

  return db
}

// Offline queue functions
// iOS Safari bug: Blobs stored directly in IndexedDB get backed by disk-cache
// files that WebKit can evict under memory/storage pressure. We store raw
// ArrayBuffer bytes instead (inline in the IDB record) and reconstruct the Blob
// on read. See bugs.webkit.org/show_bug.cgi?id=198278.
export interface OfflineQueueItem {
  id?: number
  timestamp: Date
  image: ArrayBuffer
  imageType: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy queue stores arbitrary settings from multiple call sites
  settings: any
  status: 'pending' | 'processing' | 'completed' | 'error'
  error?: string
  retryCount: number
  locationDetails?: string
  description?: string
  projectName?: string
  inspectionId?: string
  roomId?: string
  roomLabel?: string
  // Set after /api/uploads succeeds. When present, retries skip re-upload and
  // resume at the follow-up step, preventing duplicate analysis rows.
  uploadedAnalysisId?: string
  ticketData?: TicketQueueData
}

export interface TicketQueueData {
  contractorId: string
  deadline: string
  notes: string
  type: 'REQUEST'
  requestDescription: string
}

// Shape returned by meta-only reads — no heavy image payload.
export type OfflineQueueItemMeta = Omit<OfflineQueueItem, 'image' | 'imageType'>

export interface OfflineQueueMetadata {
  locationDetails?: string
  description?: string
  projectName?: string
  inspectionId?: string
  roomId?: string
  roomLabel?: string
  ticketData?: TicketQueueData
}

export async function addToOfflineQueue(
  image: Blob,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy queue accepts arbitrary settings shape from multiple call sites
  settings: any,
  metadata?: OfflineQueueMetadata
): Promise<number> {
  const db = await initDB()
  const buffer = await image.arrayBuffer()
  return await db.add('offlineQueue', {
    timestamp: new Date(),
    image: buffer,
    imageType: image.type || 'image/jpeg',
    settings,
    status: 'pending',
    retryCount: 0,
    locationDetails: metadata?.locationDetails,
    description: metadata?.description,
    projectName: metadata?.projectName,
    inspectionId: metadata?.inspectionId,
    roomId: metadata?.roomId,
    roomLabel: metadata?.roomLabel,
    ticketData: metadata?.ticketData,
  })
}

/**
 * Phase 3: store a blob reserved for the intent queue, keyed by analysisId.
 * Uses status='completed' so OfflineQueueManager.processQueue ignores it —
 * the intent worker looks it up via by-analysisId index.
 */
export async function storeBlobForIntent(
  analysisId: string,
  image: Blob,
): Promise<number> {
  const db = await initDB()
  const buffer = await image.arrayBuffer()
  return await db.add('offlineQueue', {
    timestamp: new Date(),
    image: buffer,
    imageType: image.type || 'image/jpeg',
    settings: {},
    status: 'completed',
    retryCount: 0,
    uploadedAnalysisId: analysisId,
  })
}

/**
 * Returns queue items WITHOUT image bytes — for status UI and iteration.
 * Use getOfflineQueueItem(id) to fetch a single item's image right before use.
 */
export async function getOfflineQueue(statusFilter?: 'pending' | 'processing' | 'error'): Promise<OfflineQueueItemMeta[]> {
  const db = await initDB()
  let items: OfflineQueueItem[]
  if (statusFilter) {
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    const index = db.transaction('offlineQueue', 'readonly').store.index('by-status')
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    items = await index.getAll(statusFilter)
  } else {
    items = await db.getAll('offlineQueue')
  }
  return items.map(({ image: _img, imageType: _type, ...meta }) => meta as OfflineQueueItemMeta)
}

/** Full item including image bytes — call right before upload to bound memory. */
export async function getOfflineQueueItem(id: number): Promise<OfflineQueueItem | undefined> {
  const db = await initDB()
  return await db.get('offlineQueue', id)
}

export async function updateOfflineQueueItem(id: number, updates: Partial<OfflineQueueItem>): Promise<void> {
  const db = await initDB()
  const item = await db.get('offlineQueue', id)
  if (item) {
    await db.put('offlineQueue', { ...item, ...updates })
  }
}

export async function removeFromOfflineQueue(id: number): Promise<void> {
  const db = await initDB()
  await db.delete('offlineQueue', id)
}

/**
 * Delete legacy entries where `image` is a Blob (pre-ArrayBuffer-migration).
 * These are unrecoverable on iOS Safari once WebKit has evicted the backing
 * file. Returns the number of entries purged.
 */
export async function purgeLegacyBlobQueueItems(): Promise<number> {
  const db = await initDB()
  const all = await db.getAll('offlineQueue')
  let purged = 0
  for (const item of all) {
    if (item.image instanceof Blob || !(item.image instanceof ArrayBuffer)) {
      await db.delete('offlineQueue', item.id!)
      purged++
    }
  }
  return purged
}

/** Look up a pending/in-flight blob by analysisId (Phase 3 — upload intent handler). */
export async function getOfflineQueueItemByAnalysisId(analysisId: string): Promise<OfflineQueueItem | undefined> {
  const db = await initDB()
  // @ts-expect-error — index typed loosely
  const index = db.transaction('offlineQueue', 'readonly').store.index('by-analysisId')
  // @ts-expect-error -- idb generic typing rejects dynamic index access
  return await index.get(analysisId)
}

// Intent queue (Phase 3)

// 2 min: bounded by intent-worker's 90s fetch timeout + 120s tick watchdog,
// plus headroom. Anything older than this is a tab crash, not a long upload.
const STUCK_INTENT_THRESHOLD_MS = 2 * 60 * 1000

export async function addIntent(record: IntentRecord): Promise<void> {
  const db = await initDB()
  await db.put('intents', record)
}

export async function getIntent(id: string): Promise<IntentRecord | undefined> {
  const db = await initDB()
  return await db.get('intents', id)
}

export async function listIntents(filter?: { status?: IntentStatus; type?: IntentType }): Promise<IntentRecord[]> {
  const db = await initDB()
  if (filter?.status) {
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    const index = db.transaction('intents', 'readonly').store.index('by-status')
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    const rows: IntentRecord[] = await index.getAll(filter.status)
    if (filter.type) return rows.filter((r) => r.type === filter.type)
    return rows
  }
  if (filter?.type) {
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    const index = db.transaction('intents', 'readonly').store.index('by-type')
    // @ts-expect-error -- idb generic typing rejects dynamic index access
    return await index.getAll(filter.type)
  }
  return await db.getAll('intents')
}

export async function updateIntent(id: string, patch: Partial<IntentRecord>): Promise<IntentRecord | undefined> {
  const db = await initDB()
  const current = await db.get('intents', id)
  if (!current) return undefined
  const next: IntentRecord = { ...current, ...patch, updatedAt: Date.now() }
  await db.put('intents', next)
  return next
}

export async function deleteIntent(id: string): Promise<void> {
  const db = await initDB()
  await db.delete('intents', id)
}

/**
 * Re-pend `in_flight` intents back to `pending`.
 *
 * Mid-session callers pass `force=false` (default) — only re-pend intents
 * older than STUCK_INTENT_THRESHOLD_MS so we don't snatch a genuinely
 * in-progress fetch out from under its own handler.
 *
 * Boot-time callers pass `force=true` — every `in_flight` row in the
 * database at startup is by definition a crash survivor, so the threshold
 * is meaningless and just delays recovery by up to 2 minutes for the
 * common case (a tab that crashed seconds before reload).
 */
export async function recoverStuckIntents(force = false): Promise<number> {
  const db = await initDB()
  // @ts-expect-error -- idb generic typing rejects dynamic index access
  const index = db.transaction('intents', 'readwrite').store.index('by-status')
  // @ts-expect-error -- idb generic typing rejects dynamic index access
  const inFlight: IntentRecord[] = await index.getAll('in_flight' as IntentStatus)
  const cutoff = Date.now() - STUCK_INTENT_THRESHOLD_MS
  let recovered = 0
  for (const intent of inFlight) {
    const since = intent.inFlightSince || intent.updatedAt
    if (force || since < cutoff) {
      await db.put('intents', {
        ...intent,
        status: 'pending',
        inFlightSince: undefined,
        updatedAt: Date.now(),
      })
      recovered++
    }
  }
  return recovered
}

/**
 * Delete intents in a terminal state whose updatedAt is older than the cutoff.
 * Used for both `done` (24h retention) and `failed` (3d retention) — without
 * periodic purging, markFailed(terminal=true) rows accumulate in IDB forever.
 */
export async function purgeIntentsByStatus(status: IntentStatus, olderThanMs: number): Promise<number> {
  const db = await initDB()
  // @ts-expect-error -- idb generic typing rejects dynamic index access
  const index = db.transaction('intents', 'readwrite').store.index('by-status')
  // @ts-expect-error -- idb generic typing rejects dynamic index access
  const rows: IntentRecord[] = await index.getAll(status)
  const cutoff = Date.now() - olderThanMs
  let purged = 0
  for (const intent of rows) {
    if (intent.updatedAt < cutoff) {
      await db.delete('intents', intent.id)
      purged++
    }
  }
  return purged
}

export const purgeDoneIntents = (olderThanMs: number) => purgeIntentsByStatus('done', olderThanMs)
export const purgeFailedIntents = (olderThanMs: number) => purgeIntentsByStatus('failed', olderThanMs)

/** Nuclear: wipe every intent regardless of state. Exposed for the debug panel. */
export async function purgeAllIntents(): Promise<number> {
  const db = await initDB()
  const all: IntentRecord[] = await db.getAll('intents')
  for (const intent of all) await db.delete('intents', intent.id)
  return all.length
}

// Defect solutions functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- DefectSolution shape lives in defect-solutions.ts; importing here would create a cycle
export async function saveDefectSolution(solution: any): Promise<void> {
  const db = await initDB()
  await db.put('defectSolutions', solution)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see saveDefectSolution
export async function getDefectSolution(defectType: string): Promise<any> {
  const db = await initDB()
  return await db.get('defectSolutions', defectType)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- see saveDefectSolution
export async function getAllDefectSolutions(): Promise<any[]> {
  const db = await initDB()
  return await db.getAll('defectSolutions')
}

